import { Fragment } from "react";
import { Tooltip } from "src/components/Tooltip/Tooltip";
import { Color, Flex, Icon, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import type { FallbackRelaxation } from "src/utils/pogWarning";
import styled from "styled-components";

const KNOWN_RUNGS = [
  "fallback_place_all",
  "fallback_variant_fences",
  "fallback_block_shapes",
  "fallback_mudmap",
  "fallback_greedy_fill",
  "fallback_cp_model_presolve",
] as const;

const isKnownRung = (type: string): type is (typeof KNOWN_RUNGS)[number] =>
  (KNOWN_RUNGS as readonly string[]).includes(type);

const Chip = styled(Flex)`
  padding: 2px 6px;
  gap: 4px;
  align-items: center;
  border: 1px solid ${Color.yellow};
  border-radius: 10px;
  background-color: ${Color.white};
  cursor: default;
`;

interface Props {
  rungs: FallbackRelaxation[];
}

/** The pogger only returned this pog by relaxing rules (DEV-3531): which, and why. */
export const RelaxedToFinish = ({ rungs }: Props) => {
  const { t } = useTranslation("common");
  if (rungs.length === 0) return null;

  const describeRule = (rule: string) => {
    switch (rule) {
      case "placeAllCdt1s":
        return t("pogWarning.relaxedToFinish.rules.placeAllCdt1s");
      case "placeAllVariants":
        return t("pogWarning.relaxedToFinish.rules.placeAllVariants");
      case "strictRectangles":
        return t("pogWarning.relaxedToFinish.rules.strictRectangles");
      case "main solve":
        return t("pogWarning.relaxedToFinish.rules.mainSolve");
      case "generated mudmap":
        return t("pogWarning.relaxedToFinish.rules.generatedMudmap");
      default:
        return t("pogWarning.relaxedToFinish.rules.other");
    }
  };

  const describeError = (error: string) => {
    const normalized = error.toLowerCase();
    if (normalized.includes("infeasible")) {
      return t("pogWarning.relaxedToFinish.errors.infeasible");
    }
    if (normalized.includes("timeout") || normalized.includes("timed out")) {
      return t("pogWarning.relaxedToFinish.errors.timeout");
    }
    return t("pogWarning.relaxedToFinish.errors.other");
  };

  return (
    <Chip data-testid="relaxed-to-finish">
      <Icon name="alert" color={Color.yellow} size={14} />

      <Text variant="caption3" bold color={Color.textMain} whiteSpace="nowrap">
        {t("pogWarning.relaxedToFinish.label")}
      </Text>

      <Tooltip textAlign="left">
        {t("pogWarning.relaxedToFinish.intro")}
        {rungs.map((rung, index) => (
          <Fragment key={index}>
            <br />
            {t("pogWarning.relaxedToFinish.rung", {
              index: index + 1,
              rung: isKnownRung(rung.type)
                ? t(`pogWarning.relaxedToFinish.rungs.${rung.type}`)
                : t("pogWarning.relaxedToFinish.rungs.other"),
              rules: rung.rules.map(describeRule).join(", "),
            })}
            {rung.after && (
              <>
                <br />
                {t("pogWarning.relaxedToFinish.after", { error: describeError(rung.after) })}
              </>
            )}
          </Fragment>
        ))}
      </Tooltip>
    </Chip>
  );
};
